import { useCallback, useRef, useState } from 'react';
import { useDismiss } from '../useDismiss';
import { Icon, type IconName } from './Icon';

/**
 * The shell every row menu shares: where it opens, how it dismisses, how an item looks.
 *
 * Extracted when the goals screen needed the same thing the task menu had. Two menus with two
 * dismissal contracts is how one of them ends up not closing on Escape.
 *
 * No roving tabindex or focus trap, and that is a decision rather than an omission: nothing is
 * reachable *only* from a menu built on this. The items are ordinary buttons in DOM order, so Tab
 * reaches them once it is open, and every action also has a longer path elsewhere. If a menu ever
 * becomes the only route to something, that stops being true and it needs the full APG treatment.
 */

export type MenuItem =
  | { key: string; divider: true }
  | { key: string; icon: IconName; label: string; danger?: boolean; run: () => void };

export interface MenuAnchor {
  x: number;
  y: number;
}

/** Opening state. `open` takes the event so a right-click and a button press look the same here. */
export function useMenuAnchor<T>() {
  const [state, setState] = useState<{ subject: T; anchor: MenuAnchor } | null>(null);

  const openAt = useCallback((event: React.MouseEvent, subject: T) => {
    event.preventDefault();
    event.stopPropagation();
    setState({ subject, anchor: { x: event.clientX, y: event.clientY } });
  }, []);

  /** For a `⋯` button: open under it rather than at the pointer, so it lines up with its trigger. */
  const openUnder = useCallback((event: React.MouseEvent, subject: T) => {
    event.preventDefault();
    event.stopPropagation();
    const r = (event.currentTarget as HTMLElement).getBoundingClientRect();
    setState({ subject, anchor: { x: r.left, y: r.bottom + 2 } });
  }, []);

  const close = useCallback(() => setState(null), []);
  return { state, openAt, openUnder, close };
}

/** Roughly the menu's size, used to decide whether to flip it. Exact enough for a corner. */
const MENU_W = 190;
const MENU_H = 260;

export function MenuSurface({
  anchor,
  label,
  items,
  onClose,
  note,
}: {
  anchor: MenuAnchor | null;
  label: string;
  items: MenuItem[];
  onClose: () => void;
  /** A line above the items saying what they will act on, when that is not obvious. */
  note?: string;
}) {
  const box = useRef<HTMLDivElement>(null);

  /*
    Capturing scroll, and closing rather than following: the menu is placed in viewport coordinates
    and the surfaces that raise it scroll independently, so a menu that chased them would be worse.

    The click is deliberately *not* swallowed — clicking another row's menu button should open that
    row's menu. The filter panel takes the other policy, for the reason in `useDismiss`.
  */
  useDismiss(anchor !== null, onClose, [box], { onScroll: true });

  if (!anchor) return null;

  // Flipped rather than clipped when the click lands near an edge.
  const left = Math.min(anchor.x, window.innerWidth - MENU_W - 8);
  const top = anchor.y + MENU_H > window.innerHeight ? Math.max(8, anchor.y - MENU_H) : anchor.y;

  return (
    <div ref={box} className="task-menu card" role="menu" aria-label={label} style={{ left, top }}>
      {note !== undefined && <p className="subtext menu-note">{note}</p>}
      {items.map((item) =>
        'divider' in item ? (
          <hr key={item.key} />
        ) : (
          <button
            key={item.key}
            type="button"
            role="menuitem"
            className={item.danger ? 'task-menu-item is-danger' : 'task-menu-item'}
            onClick={item.run}
          >
            <Icon name={item.icon} size={14} />
            {item.label}
          </button>
        ),
      )}
    </div>
  );
}
